import { DefaultButton, Dialog, DialogFooter, DialogType, IDialogContentProps, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyGetItemByUniqueIDQuery, useLazySendSharepointItemToRecyclebinQuery } from "../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { IDocUrlDropdown } from "../../src/taskpane/interfaces/IDocUrlDropdown";
import authModule from "../../utilities/AuthModule";
import { getApiErrorMessage, ItemError } from "../../utilities/ApiErrorHelper";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { getSharePointSite } from "../../src/taskpane/services/GraphService";

export interface ICMDeleteModalProps {
    selectedItems?: any[];
    selectedGraphItem?: any;
    showDialog: boolean;
    selectedDocumentLibraries?: IDocUrlDropdown[];
    closeDialog: () => void;
    closeOnSuccess: (successIds: string[], errors: ItemError[]) => void;
}

export const CMDeleteModal = ({ selectedItems, selectedGraphItem, showDialog, selectedDocumentLibraries, closeDialog, closeOnSuccess }: ICMDeleteModalProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories.length > 0 ? state.workingDirectories?.workingDirectories[state.workingDirectories?.workingDirectories?.length - 1] : null);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories?.oneDriveRootDetails);
    const oneDriveWebUrl = useSelector((state: RootState) => state.user.oneDriveWebUrl);

    const [isBusy, setIsBusy] = React.useState(false);

    const isInOneDrive = React.useRef(currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder);

    const [sendSharepointItemToRecyclebin] = useLazySendSharepointItemToRecyclebinQuery();
    const [getItemByUniqueId] = useLazyGetItemByUniqueIDQuery();

    const dialogContentProps: IDialogContentProps = {
        type: DialogType.normal,
        title: languageStrings.Delete,
        subText: languageStrings.DeleteSPItemConfirmMessage,
        showCloseButton: true,
        closeButtonAriaLabel: 'Close'
    };

    const onConfirmDelete = async () => {
        setIsBusy(true);
        if (selectedItems?.length > 0) {
            let successIds: string[] = [];
            let errors: ItemError[] = [];

            await Promise.all(selectedItems.map(async (item) => {
                if (selectedDocumentLibraries) {
                    try {
                        const selectedLibrary = selectedDocumentLibraries.find(x => x.driveId === item?.driveid);

                        let file = await getItemByUniqueId({
                            siteUrl: selectedLibrary.webUrl,
                            sharePointListId: selectedLibrary.sharePointListId,
                            uniqueId: item.uniqueid,
                            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                        }).unwrap();

                        await sendSharepointItemToRecyclebin({
                            siteUrl: selectedLibrary.webUrl,
                            sharePointListId: selectedLibrary.sharePointListId,
                            accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            itemId: file.ID
                        }).unwrap();

                        successIds.push(file.ID)
                    } catch (error) {
                        errors.push({ name: item?.name ?? item?.filename ?? item?.title, message: getApiErrorMessage(error) });
                    }

                } else {
                    try {
                        await sendSharepointItemToRecyclebin({
                            siteUrl: isInOneDrive?.current ? oneDriveRootDetails?.sharePointSiteUrl : currentDirectory?.webUrl,
                            sharePointListId: isInOneDrive?.current ? oneDriveRootDetails?.sharePointListId : currentDirectory.sharePointListId,
                            accessToken: isInOneDrive?.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                            itemId: item.ID
                        }).unwrap();

                        successIds.push(item.ID);
                    } catch (error) {
                        errors.push({ name: item?.FileLeafRef ?? item?.Title, message: getApiErrorMessage(error) });
                    }
                }
            }));
            setIsBusy(false);
            closeOnSuccess(successIds, errors);
        } else if (selectedGraphItem) {
            let errors: ItemError[] = [];
            try {
                const isInOneDrive: boolean = selectedGraphItem.resource.webUrl?.startsWith(oneDriveWebUrl);
                const sharePointSite = await getSharePointSite(selectedGraphItem.resource.parentReference.siteId);

                await sendSharepointItemToRecyclebin({
                    siteUrl: sharePointSite.webUrl,
                    sharePointListId: selectedGraphItem.resource.parentReference.sharepointIds?.listId,
                    accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    itemId: selectedGraphItem.resource.parentReference.sharepointIds?.listItemId
                }).unwrap();
            } catch (error) {
                errors.push({ name: selectedGraphItem?.resource?.name, message: getApiErrorMessage(error) });
            }
            setIsBusy(false);
            closeOnSuccess([], errors);
        } else {
            // Nothing to delete — never leave the dialog stuck in the busy state
            setIsBusy(false);
            closeDialog();
        }
    }

    return (
        <Dialog hidden={!showDialog} onDismiss={() => closeDialog()} dialogContentProps={dialogContentProps} >
            <DialogFooter>
                <PrimaryButton autoFocus onClick={() => onConfirmDelete()} text={languageStrings.Delete} disabled={isBusy}>
                    {isBusy && <Spinner size={SpinnerSize.small} style={{ paddingLeft: '5px' }} />}
                </PrimaryButton>
                <DefaultButton onClick={() => closeDialog()} text={languageStrings.Cancel} disabled={isBusy} />
            </DialogFooter>
        </Dialog>
    )
};